import React, { useState } from "react";
import { Api } from "../api";
import { useTranslation } from "react-i18next";
import Languages from "../Navbar/Languges";
import Logo from '../../Images/logo.png';
import { Lock, Phone, AlertCircle } from "lucide-react";

const Login = () => {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { t, i18n } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const response = await Api.post("/login", { phone, password });
      localStorage.setItem("token", response.data.token);
      window.location.href = "/";
    } catch (error) {
      setError(error.response?.data?.status || error.response?.data || error.message);
      console.error("Error:", error.response?.data || error.message);
    }
  };

  const isRtl = i18n.language === "ar" || i18n.language === "ckb";

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/50 flex items-center justify-center p-4 sm:p-6 select-none overflow-hidden">
      
      {/* Absolute Language Switcher */}
      <div className={`absolute top-4 ${isRtl ? "left-4" : "right-4"} sm:top-6 sm:right-6 z-20`}>
        <Languages />
      </div>

      {/* Decorative Blur Orbs */}
      <div className="absolute top-[-20%] left-[-10%] w-[50vw] h-[50vw] bg-indigo-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[50vw] h-[50vw] bg-violet-200/20 rounded-full blur-3xl pointer-events-none" />

      {/* Login Card */}
      <div className="relative w-full max-w-md bg-white border border-slate-100/90 shadow-2xl rounded-3xl p-8 sm:p-10 z-10 transition-all duration-300">
        
        {/* Logo and Header */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-50 to-indigo-100/60 rounded-2xl flex items-center justify-center shadow-inner mb-4">
            <img src={Logo} alt="Nergz Logo" className="w-11 h-11 object-contain drop-shadow" />
          </div>
          <h2 className="text-2xl font-extrabold text-slate-800 tracking-tight">
            {t("login")}
          </h2>
          <p className="text-sm text-slate-400 mt-1.5 font-medium">
            {t("welcome_back") || "Please sign in to your account"}
          </p>
        </div>

        {/* Error Alert Box */}
        {error && (
          <div className="p-4 mb-5 text-sm font-semibold text-red-600 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-2.5 animate-pulse">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span className="truncate">{t(error) || error}</span>
          </div>
        )}

        {/* Input Fields */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="phone" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
              {t("phone")}
            </label>
            <div className="relative">
              <span className={`absolute inset-y-0 ${isRtl ? "right-4" : "left-4"} flex items-center text-slate-400`}>
                <Phone className="w-4 h-4" />
              </span>
              <input
                type="text"
                id="phone"
                name="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder={t("enter_phone") || "0770..."}
                className={`w-full ${isRtl ? "pr-11 pl-4" : "pl-11 pr-4"} py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all duration-200 text-sm`}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
              {t("password")}
            </label>
            <div className="relative">
              <span className={`absolute inset-y-0 ${isRtl ? "right-4" : "left-4"} flex items-center text-slate-400`}>
                <Lock className="w-4 h-4" />
              </span>
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder={t("enter_password") || "••••••••"}
                className={`w-full ${isRtl ? "pr-11 pl-4" : "pl-11 pr-4"} py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all duration-200 text-sm`}
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="w-full py-3.5 px-4 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-bold rounded-xl shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/30 transition-all duration-200 active:scale-[0.98] cursor-pointer text-sm mt-2"
          >
            {t("login")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
